import React, { FormEvent, useState } from 'react'
import classNames from 'classnames'

import FieldWrapper from '../FieldWrapper'
import RequiredField from '../../RequiredField'

import style from './style.scss'

/**
 * InputField is a controlled input within React. It relies on a parent
 * component to manage the state and then pass that state down to the
 * input as a prop. The Input's onChange function will pass the current
 * text value to the onUpdateField function for managing the state.
 */
export type InputType = 'email' | 'text' | 'password' | 'tel'

export type InputFieldType = {
  customValidation?: (arg0: string) => boolean
  disabled?: boolean
  errorText?: string
  inputId: string
  isLabelHidden?: boolean
  isRequired?: boolean
  label: string
  maxLength?: number
  minLength?: number
  placeholder?: string
  type?: InputType
  value: string
}

type Props = InputFieldType & {
  className?: string
  onUpdateField: (arg0: string) => void
}

InputField.defaultProps = {
  className: '',
  customValidation: undefined,
  disabled: false,
  errorText: '',
  isLabelHidden: false,
  isRequired: false,
  maxLength: undefined,
  minLength: undefined,
  placeholder: undefined,
  type: 'text',
}

function InputField(props: Props) {
  const {
    className,
    customValidation,
    disabled,
    isLabelHidden,
    inputId,
    isRequired,
    label,
    maxLength,
    minLength,
    placeholder,
    type,
    onUpdateField,
    errorText,
    value,
  } = props

  // Prevents validation from firing when no changes happen.
  const [currentValue, updateValue] = useState<string>()

  const handleValidation = (event: FormEvent<HTMLInputElement>) => {
    if (currentValue === event.currentTarget.value || !customValidation) {
      return
    }
    updateValue(event.currentTarget.value)
    if (event.currentTarget.value === '') {
      event.currentTarget.setCustomValidity('')
      return
    }

    const isValid = customValidation(event.currentTarget.value)

    if (!isValid && errorText) {
      event.currentTarget.setCustomValidity(errorText)
    } else {
      event.currentTarget.setCustomValidity('')
    }
  }

  const handleChange = (event: FormEvent<HTMLInputElement>) => {
    if (customValidation && errorText) {
      event.currentTarget.setCustomValidity('')
    }
    return onUpdateField(event.currentTarget.value)
  }

  return (
    <FieldWrapper className={className}>
      <label
        className={classNames(style.label, {
          [style.hideLabel]: isLabelHidden,
        })}
        htmlFor={inputId}
      >
        {label}
        {isRequired && <RequiredField />}
      </label>
      <input
        aria-label={label}
        className={style.input}
        disabled={disabled}
        id={inputId}
        maxLength={maxLength}
        minLength={minLength}
        placeholder={placeholder}
        required={isRequired}
        type={type}
        value={value}
        onBlur={customValidation ? handleValidation : undefined}
        onChange={handleChange}
        onInvalid={
          errorText
            ? (evt) => {
                evt.currentTarget.setCustomValidity(errorText)
              }
            : undefined
        }
      />
    </FieldWrapper>
  )
}

export default InputField
